<template>
  <container>
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Total Participants</h1>
        <p class="pg-title-sub">Review participant totals by live session and date range</p>
      </div>
    </div>

    <section class="tp-filter-card">
      <div class="tp-card-header">
        <span class="tp-icon-pill">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <path d="M16 2v4M8 2v4M3 10h18"/>
          </svg>
        </span>
        <div class="tp-header-copy">
          <h2 class="tp-card-title">Date Range</h2>
          <p class="tp-card-subtitle">Filter sessions by start and end date</p>
        </div>
      </div>
      <div class="tp-date-row">
        <date-range-field v-model="rangeData"></date-range-field>
      </div>
    </section>

    <section class="tp-table-card">
      <div class="tp-card-header tp-card-header--split">
        <div class="tp-header-left">
          <span class="tp-icon-pill">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </span>
          <div class="tp-header-copy">
            <h2 class="tp-card-title">Participant Sessions</h2>
            <p class="tp-card-subtitle">Click a row to open the session detail in a new tab</p>
          </div>
        </div>
        <div class="tp-summary-strip">
          <span class="tp-summary-pill">
            <span class="tp-summary-label">Rows</span>
            <strong>{{ loading ? '...' : tableData.length }}</strong>
          </span>
          <span class="tp-summary-pill tp-summary-pill--primary">
            <span class="tp-summary-label">Total TP</span>
            <strong>{{ loading ? '...' : formattedTotalParticipants }}</strong>
          </span>
        </div>
      </div>

      <div v-if="loading" class="tp-skeleton-table">
        <div class="tp-sk-head">
          <div v-for="n in 5" :key="'tp-head-' + n" class="tp-sk-cell sk-shimmer"></div>
        </div>
        <div v-for="row in 6" :key="'tp-row-' + row" class="tp-sk-row">
          <div v-for="col in 5" :key="'tp-row-' + row + '-col-' + col" class="tp-sk-cell sk-shimmer"></div>
        </div>
      </div>

      <div v-else-if="tableData.length" class="tp-table-wrap">
        <v-table
          class="tp-easy-table"
          is-horizontal-resize
          :columns="columns"
          :table-data="tableData"
          :show-vertical-border="true"
          :title-row-height="38"
          :border="1"
          :footer="footer"
          :footer-row-height="42"
          :footer-cell-class-name="setFooterCellClass"
          row-hover-color="#fafafa"
          :row-click="raceDetail"
        ></v-table>
      </div>

      <div v-else class="tp-empty">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
        <p>No participant sessions found for this date range.</p>
      </div>

      <div v-if="!loading && pageTotal" class="tp-pagination">
        <v-pagination
          @page-change="pageChange"
          @page-size-change="pageSizeChange"
          :total="pageTotal"
          :pageIndex="pageIndex + 1"
          :page-size="pageSize"
          :layout="['total', 'prev', 'pager', 'next', 'sizer', 'jumper']"
        ></v-pagination>
      </div>
    </section>
  </container>
</template>

<script>
import Container from '@/components/Container'
import DateRangeField from '@/components/Forms/DateRangeField'
import 'vue-easytable/libs/themes-base/index.css'
import { VTable, VPagination } from 'vue-easytable'
import Vue from 'vue'
import axios from 'axios'
import moment from 'moment'

Vue.component(VTable.name, VTable)
Vue.component(VPagination.name, VPagination)

export default {
  name: 'tp',
  data () {
    return {
      columns: [
        { field: 'date', title: 'Date', width: 100, titleAlign: 'center', columnAlign: 'center', isFrozen: true, componentName: 'date-format' },
        { field: 'name', title: 'Session Title', width: 250, titleAlign: 'center', columnAlign: 'center', isResize: true },
        { field: 'TrainerName', title: 'Trainer', width: 120, titleAlign: 'center', columnAlign: 'center' },
        { field: 'TP', title: 'TP', width: 100, titleAlign: 'center', columnAlign: 'center' },
        { field: 'question', title: 'Question', width: 500, titleAlign: 'center', columnAlign: 'center', isResize: true }
      ],
      footer: [[]],
      tableData: [],
      pageIndex: 0,
      pageTotal: 0,
      pageSize: 10,
      totalParticipants: 0,
      rangeData: {
        startDate: moment().startOf('month').startOf('day').toISOString(),
        endDate: moment().endOf('month').endOf('day').toISOString()
      },
      loading: false
    }
  },
  computed: {
    formattedTotalParticipants () {
      return this.formatNumber(this.totalParticipants)
    }
  },
  methods: {
    intialize () {
      this.getTotalParticipants(this.rangeData.startDate, this.rangeData.endDate, this.pageIndex, this.pageSize)
    },
    getTotalParticipants (startDate, endDate, pageIndex, pageSize) {
      this.loading = true
      const AuthStr = 'Bearer '.concat(window.localStorage.getItem('default-auth-token'))

      axios.get(this.$expressApi + 'getTotalParticipants?startDate=' + startDate + '&endDate=' + endDate + '&pageIndex=' + pageIndex + '&pageSize=' + pageSize + '&staging=' + this.$isStage, { headers: { Authorization: AuthStr } })
        .then(response => {
          const content = response.data.content || []
          this.tableData = content
          this.pageTotal = response.data.totalElements || 0
          this.totalParticipants = response.data.totalTp || 0
          if (this.tableData.length > 0) {
            this.footer = [['Total', '', '', this.totalParticipants, '']]
          } else {
            this.footer = []
          }
          this.loading = false
        }, error => {
          this.tableData = []
          this.pageTotal = 0
          this.totalParticipants = 0
          this.footer = []
          console.log('error', error)
          this.loading = false
        })
    },
    pageChange (pageIndex) {
      this.pageIndex = pageIndex - 1
      this.getTotalParticipants(this.rangeData.startDate, this.rangeData.endDate, this.pageIndex, this.pageSize)
    },
    pageSizeChange (pageSize) {
      this.pageIndex = 0
      this.pageSize = pageSize
      this.getTotalParticipants(this.rangeData.startDate, this.rangeData.endDate, this.pageIndex, this.pageSize)
    },
    raceDetail (newValue, raceData) {
      let route = this.$router.resolve({
        name: 'past-run',
        params: { pastRun: true, raceId: raceData.raceId }
      })
      window.open(route.href, '_blank')
    },
    setFooterCellClass (rowIndex, colIndex, value) {
      if (colIndex === 0) {
        return 'tp-footer-cell-title'
      }
      return 'tp-footer-cell-normal'
    },
    formatNumber (value) {
      const numericValue = Number(value || 0)
      return numericValue.toLocaleString()
    }
  },
  created () {
    this.intialize()
  },
  watch: {
    rangeData () {
      this.pageIndex = 0
      this.pageSize = 10
      this.intialize()
    }
  },
  components: {
    Container,
    DateRangeField
  }
}

Vue.component('date-format', {
  template: '<span>{{ newdate }}</span>',
  data: () => ({
    newdate: ''
  }),
  props: {
    rowData: {
      type: Object
    },
    field: {
      type: String
    },
    index: {
      type: Number
    }
  },
  methods: {
    initdata () {
      this.newdate = moment(this.rowData.start_date).format('YYYY-MM-DD')
    }
  },
  created () {
    this.initdata()
  },
  watch: {
    rowData: function (val) {
      this.initdata()
    }
  }
})
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

.tp-filter-card,
.tp-table-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.tp-filter-card {
  margin-bottom: 1rem;
}

.tp-card-header,
.tp-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tp-card-header--split {
  justify-content: space-between;
  gap: 1rem;
}

.tp-icon-pill {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
}

.tp-header-copy {
  min-width: 0;
}

.tp-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.2;
  margin: 0;
}

.tp-card-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  line-height: 1.35;
  margin: 4px 0 0;
}

.tp-date-row {
  padding-top: 1rem;
}

.tp-summary-strip {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.tp-summary-pill {
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid #e8e8ed;
  border-radius: 999px;
  background: #fafafa;
  color: #1a1a2e;
}

.tp-summary-pill--primary {
  border-color: #dedafc;
  background: #f0effe;
  color: #413b94;
}

.tp-summary-label {
  color: #8a8a9a;
  font-size: 11px;
  font-weight: 600;
}

.tp-summary-pill strong {
  font-size: 13px;
  font-weight: 800;
}

.tp-table-wrap {
  width: 100%;
  margin-top: 1.25rem;
  overflow-x: auto;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
}

.tp-easy-table {
  width: 100%;
  min-width: 1080px;
  font-size: 12px;
}

.tp-table-wrap tr.v-table-header-row {
  background: #f7f7fb;
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 700;
}

.tp-table-wrap tr.v-table-row {
  cursor: pointer;
}

.tp-table-wrap .v-table-title-cell,
.tp-table-wrap .v-table-body-cell {
  border-color: #f0f0f5;
}

.tp-table-wrap .v-table-body-cell {
  color: #3d3d52;
  font-size: 12px;
}

.tp-table-wrap .v-table-row:hover {
  background: #fafafa;
}

.tp-footer-cell-title {
  background: #f0effe;
  color: #413b94;
  font-size: 13px;
  font-weight: 800;
}

.tp-footer-cell-normal {
  color: #5b4fcf;
  font-size: 13px;
  font-weight: 800;
}

.tp-pagination {
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  overflow-x: auto;
}

.tp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 220px;
  margin-top: 1.25rem;
  border: 1px dashed #d8d8e5;
  border-radius: 10px;
  background: #fafafa;
  color: #8a8a9a;
  text-align: center;
}

.tp-empty p {
  margin: 0;
  font-size: 13px;
}

.tp-skeleton-table {
  margin-top: 1.25rem;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.tp-sk-head,
.tp-sk-row {
  display: grid;
  grid-template-columns: minmax(90px, 0.7fr) minmax(210px, 1.7fr) minmax(120px, 1fr) minmax(72px, 0.6fr) minmax(260px, 2fr);
  gap: 14px;
  padding: 13px 16px;
  border-bottom: 1px solid #f0f0f5;
}

.tp-sk-row:last-child {
  border-bottom: none;
}

.tp-sk-head {
  background: #fafafa;
}

.tp-sk-cell {
  height: 14px;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

@media (max-width: 760px) {
  .pg-title-row,
  .tp-card-header--split {
    align-items: flex-start;
  }

  .tp-card-header--split {
    flex-direction: column;
  }

  .tp-summary-strip {
    justify-content: flex-start;
    width: 100%;
  }

  .tp-filter-card,
  .tp-table-card {
    padding: 1.2rem;
  }
}
</style>



// WEBPACK FOOTER //
// TP.vue?5d180e48